import React, { useState, useMemo } from 'react';
import { Search, Filter, Sparkles } from 'lucide-react';
import CreativeCard from './CreativeCard';
import { getCreativeUsageStats, sortByFreshness } from '../../utils/fatigueCalculator';

/**
 * CreativeDock Component
 * Side panel showing all creatives with usage stats and filtering
 *
 * @param {Object} props
 * @param {Array} props.creatives - All available creatives
 * @param {Array} props.scheduledPosts - All scheduled posts (for usage calculation)
 * @param {function} [props.onCreativeClick] - Callback when a creative is clicked
 * @param {function} [props.onDragStart] - Callback when drag starts
 * @param {function} [props.onDragEnd] - Callback when drag ends
 */
const CreativeDock = ({
    creatives = [],
    scheduledPosts = [],
    onCreativeClick,
    onDragStart,
    onDragEnd
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [filterMode, setFilterMode] = useState('all'); // 'all', 'fresh', 'high-fatigue'

    // Calculate usage stats for all creatives
    const creativesWithStats = useMemo(() => {
        return creatives.map(creative => ({
            ...creative,
            ...getCreativeUsageStats(creative.id, scheduledPosts)
        }));
    }, [creatives, scheduledPosts]);

    // Filter and sort creatives
    const filteredCreatives = useMemo(() => {
        let filtered = [...creativesWithStats];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(c =>
                c.title?.toLowerCase().includes(query) ||
                c.brandName?.toLowerCase().includes(query)
            );
        }

        // Apply fatigue filter
        if (filterMode === 'fresh') {
            filtered = filtered.filter(c =>
                c.fatigueLevel === 'fresh' || c.fatigueLevel === 'low'
            );
        } else if (filterMode === 'high-fatigue') {
            filtered = filtered.filter(c =>
                c.fatigueLevel === 'high' || c.fatigueLevel === 'medium'
            );
        }

        // Sort by freshness (freshest first)
        return sortByFreshness(filtered);
    }, [creativesWithStats, searchQuery, filterMode]);

    const filterOptions = [
        { value: 'all', label: 'All' },
        { value: 'fresh', label: 'Fresh' },
        { value: 'high-fatigue', label: 'High Use' }
    ];

    return (
        <div className="card" style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 0,
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border-light)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-3)'
                }}>
                    <h3 style={{ margin: 0 }}>Creatives</h3>
                    <span className="badge badge-neutral">
                        {filteredCreatives.length}
                    </span>
                </div>

                {/* Search */}
                <div style={{
                    position: 'relative',
                    marginBottom: 'var(--space-3)'
                }}>
                    <Search
                        size={16}
                        style={{
                            position: 'absolute',
                            left: 'var(--space-3)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search creatives..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="input-field"
                        style={{
                            paddingLeft: 'var(--space-8)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    />
                </div>

                {/* Filter tabs */}
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-1)',
                    background: 'var(--color-bg-tertiary)',
                    padding: 'var(--space-1)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    {filterOptions.map(option => (
                        <button
                            key={option.value}
                            onClick={() => setFilterMode(option.value)}
                            style={{
                                flex: 1,
                                padding: 'var(--space-2)',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: filterMode === option.value ? 600 : 400,
                                background: filterMode === option.value
                                    ? 'var(--color-bg-primary)'
                                    : 'transparent',
                                color: filterMode === option.value
                                    ? 'var(--color-text-primary)'
                                    : 'var(--color-text-secondary)',
                                boxShadow: filterMode === option.value
                                    ? 'var(--shadow-sm)'
                                    : 'none',
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Creative list */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--space-3)'
            }}>
                {filteredCreatives.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 'var(--space-8) var(--space-4)',
                        color: 'var(--color-text-tertiary)',
                        textAlign: 'center'
                    }}>
                        <Sparkles size={32} style={{ marginBottom: 'var(--space-2)', opacity: 0.5 }} />
                        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                            {searchQuery
                                ? 'No creatives match your search'
                                : filterMode === 'fresh'
                                    ? 'No fresh creatives available'
                                    : filterMode === 'high-fatigue'
                                        ? 'No high-usage creatives'
                                        : 'No creatives yet'}
                        </p>
                        {!searchQuery && filterMode === 'all' && (
                            <p style={{
                                margin: 'var(--space-2) 0 0 0',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                Generate some in Creative Chat!
                            </p>
                        )}
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-3)'
                    }}>
                        {filteredCreatives.map(creative => (
                            <CreativeCard
                                key={creative.id}
                                creative={creative}
                                usageData={{
                                    usageCount: creative.usageCount,
                                    lastUsedDate: creative.lastUsedDate,
                                    fatigueLevel: creative.fatigueLevel
                                }}
                                draggable={true}
                                onDragStart={onDragStart}
                                onDragEnd={onDragEnd}
                                onClick={onCreativeClick}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Footer hint */}
            <div style={{
                padding: 'var(--space-3) var(--space-4)',
                borderTop: '1px solid var(--color-border-light)',
                background: 'var(--color-bg-secondary)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                textAlign: 'center'
            }}>
                Drag creatives to calendar to schedule
            </div>
        </div>
    );
};

export default CreativeDock;
